import { MouseEvent } from 'react';
import { Disclosure } from '@headlessui/react'
import { MenuIcon, XIcon } from '@heroicons/react/outline'
import { OutboundLink } from "react-ga";
import { useTranslation } from 'react-i18next';

import { SITE_NAME } from "Config";
import { trackEvent } from "Helpers/analytics";
import GetInTouchRef from "Refs/GetInTouch";

export default function Header() {
  const { t } = useTranslation();

  const scrollToGetInTouch = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    GetInTouchRef.current?.scrollIntoView({ behavior: 'smooth' });
    trackEvent({ category: "Get In Touch", action: "Clicked" });
  }

  return (
    <Disclosure as="nav" className="bg-white shadow">
      {({ open }) => (
        <>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <a href="/">
                    <div className="flex items-center">
                      <svg className="w-8 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 273 273">
                        <g fill="none" fillRule="evenodd">
                          <path fill="#000000" d="M136.274 273c75.263 0 136.274-61.01 136.274-136.274C272.548 61.462 211.537.451 136.274.451 61.01.451 0 61.462 0 136.726 0 211.99 61.01 273 136.274 273"></path>
                          <path fill="#FFFFFE" d="M135.002 41.499c18.7.187 34.867 4.513 49.793 13.325 3.129 1.843 4.795 5.176 4.299 8.344-.585 3.752-2.947 6.472-6.479 7.289-2.201.507-4.326.198-6.3-.971-9.409-5.59-19.556-9.116-30.41-10.385-18.738-2.185-36.247 1.595-52.049 11.963-18.933 12.415-30.592 29.928-34.576 52.237-3.343 18.69.006 36.357 9.491 52.817 1.606 2.781 2.097 5.573.745 8.498-1.434 3.101-3.984 4.794-7.388 4.905-3.272.11-5.855-1.308-7.565-4.139-4.155-6.902-7.494-14.197-9.596-21.972-8.773-32.478-2.709-62.009 18.673-87.965 14.749-17.911 33.973-28.655 56.922-32.556 1.804-.309 3.614-.634 5.44-.789 3.454-.292 6.92-.469 9-.601M231.458 135.586c.017 44.855-29.697 83.011-72.455 93.5-24.234 5.948-47.387 2.561-69.282-9.391-3.217-1.76-5.054-5.303-4.563-8.52.568-3.729 3.068-6.5 6.676-7.31 2.169-.487 4.216-.079 6.152 1.02 8.995 5.07 18.646 8.227 28.875 9.452 13.707 1.645 27.06.017 39.708-5.556 25.784-11.362 41.482-31.044 46.686-58.7 3.404-18.109.221-35.375-8.663-51.548-2.489-4.529-1.209-9.573 3.062-11.951 4.254-2.373 9.237-.756 11.753 3.752 6.738 12.068 10.589 25.012 11.742 38.763.21 2.467.237 4.949.309 6.489"></path>
                          <path fill="#FFFFFE" d="M136.355 69.049c5.711 7.747 11.256 15.218 16.741 22.733 6.583 9.017 12.598 18.419 17.465 28.445 7.487 15.417 6.665 30.431-3.874 44.286-6.003 7.891-14.237 12.454-24.02 14.104-1.153.193-1.296.624-1.291 1.617.033 5.7.039 11.405-.011 17.111-.022 2.229-1.484 3.983-3.587 4.568-2.08.575-4.331-.187-5.413-2.069-.496-.855-.81-1.937-.822-2.924-.077-5.458-.038-10.909-.038-16.366v-1.33c-3.581-1.126-7.107-1.849-10.313-3.294-13.314-5.998-21.101-16.366-23.23-30.795-1.308-8.856.838-17.183 5.236-24.831 3.94-6.864 8.238-13.524 12.542-20.168 6.28-9.678 12.703-19.262 19.07-28.885.403-.618.849-1.214 1.545-2.202zm5.088 99.63c.529-.071.877-.077 1.203-.159 11.03-2.815 18.484-9.569 21.563-20.543 2.488-8.884.96-17.399-3.537-25.487-5.639-10.137-11.852-19.893-18.7-29.251-.115-.154-.319-.248-.529-.402v75.842zm-33.345-33.14c-3.068 17.851 10.777 31.744 23.407 32.981 0-2.136.071-4.266-.039-6.39-.044-.757-.353-1.65-.855-2.202-4.894-5.396-9.855-10.737-14.805-16.084-2.516-2.721-5.037-5.43-7.708-8.305zm23.407 10.671c0-4.502.038-8.552-.039-12.602-.017-.684-.375-1.529-.86-2.02-4.299-4.392-8.663-8.718-13.012-13.06-.369-.37-.755-.723-1.274-1.209-1.54 2.616-3.002 5.11-4.475 7.62 6.527 7.057 12.944 14.005 19.66 21.271zm-.066-52.005l-9.326 14.28c3.184 3.239 6.257 6.351 9.326 9.469V94.205z"></path>
                        </g>
                      </svg>
                      <svg className="w-auto h-4 text-black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 678 141">
                        <path fill="currentColor" d="M0 .539C.367.527.734.506 1.101.506 19.252.504 37.403.431 55.555.525c17.103.089 32.19 5.808 45.11 16.985 12.054 10.429 19.873 23.511 22.838 39.179 4.428 23.391-1.355 44.143-17.374 61.803-9.963 10.982-22.417 17.848-36.938 20.912a64.369 64.369 0 01-13.295 1.406c-18.267.01-36.531.005-54.795.005-.367 0-.734.017-1.101.026V.539zm22.067 22.046v96.147h1.07c10.883 0 21.766.025 32.649-.023a44.116 44.116 0 006.412-.506c11.113-1.689 20.555-6.659 28.054-15.015 11.109-12.374 14.963-26.833 11.544-43.108-1.973-9.387-6.646-17.371-13.527-24.023-9.015-8.715-19.842-13.272-32.382-13.442-10.909-.146-21.823-.03-32.734-.03h-1.086zM567.396.54h21.961v1.176c0 28.075-.101 56.152.045 84.227.068 12.94 6.011 22.89 17.345 28.971 12.028 6.453 24.07 5.469 35.176-2.483 8.894-6.368 13.223-15.415 13.263-26.293.105-28.105.035-56.209.035-84.314V.578h21.976v.949c0 28.558.135 57.115-.062 85.67-.096 13.704-5.062 25.749-14.293 35.898-9.057 9.963-20.345 15.936-33.732 17.484-16.877 1.952-31.725-2.883-44.221-14.427-9.288-8.583-14.843-19.266-16.823-31.755a52.136 52.136 0 01-.642-8.016c-.045-28.191-.028-56.381-.028-84.569V.54M384.063.504h1.049c7.266 0 14.532.014 21.799-.021.827-.003 1.224.223 1.561 1.003 11.917 27.542 23.859 55.071 35.8 82.603.152.357.314.71.533 1.209.179-.334.304-.534.398-.749 9.389-21.645 18.779-43.291 28.167-64.939 2.667-6.145 5.331-12.287 7.99-18.434.179-.414.355-.689.905-.687 7.579.022 15.156.015 22.733.017.162 0 .324.035.604.068-20.237 46.727-40.446 93.391-60.764 140.3L384.063.504M328.108 70.288c-.287 19.026-6.31 34.965-18.777 48.26-11.368 12.123-25.418 19.201-41.914 21.277-13.336 1.679-26.135-.364-38.361-5.963-5.43-2.485-7.786-8.903-5.314-14.399 2.45-5.444 8.898-7.958 14.349-5.524 4.282 1.915 8.703 3.333 13.349 4.01 11.474 1.675 22.248-.446 32.019-6.615 14.048-8.866 21.547-21.794 22.596-38.374.489-7.735-1.166-15.107-4.355-22.15-3.361-7.421 2.027-15.804 10.114-15.617 4.378.102 7.715 2.192 9.618 6.152 3.299 6.855 5.309 14.112 6.142 21.674.296 2.691.402 5.404.534 7.269M188.258 66.847c.971-17.152 7.127-32.227 19.111-44.758C219.39 9.517 234.188 2.524 251.481.955c12.447-1.131 24.383 1.03 35.746 6.26 5.437 2.502 7.807 8.987 5.302 14.405-2.551 5.519-9.018 7.965-14.516 5.451-5.829-2.667-11.936-4.364-18.335-4.425-19.48-.184-34.21 8.19-43.863 25.145-3.962 6.96-5.7 14.617-5.681 22.614.016 6.899 1.559 13.498 4.414 19.793 3.118 6.879-.948 14.391-8.404 15.591-4.598.738-9.4-1.881-11.394-6.22-4.165-9.061-6.375-18.565-6.49-28.547-.01-.678-.002-1.353-.002-2.03v-2.145"></path>
                      </svg>
                      <span className="sr-only">{SITE_NAME}</span>
                    </div>
                  </a>
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                  <a
                    href="/"
                    className="border-blue-500 text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 font-medium"
                  >
                    {t('navigation.carbon_cap')}
                  </a>
                  <OutboundLink
                    eventLabel="calculator.dovu.earth"
                    to="https://calculator.dovu.earth/"
                    target="_blank"
                    rel="nofollow"
                    className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 font-medium"
                  >
                    {t("navigation.calculator")}
                  </OutboundLink>
                  <OutboundLink
                    eventLabel="docs.dovu.market"
                    to="https://docs.dovu.market/"
                    target="_blank"
                    rel="nofollow"
                    className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 font-medium"
                  >
                    {t('navigation.developer')}
                  </OutboundLink>
                  <OutboundLink
                    eventLabel="dovu.earth"
                    to="https://dovu.earth/"
                    target="_blank"
                    rel="nofollow"
                    className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 font-medium"
                  >
                    {t('navigation.learn')}
                  </OutboundLink>
                </div>
              </div>
              <div className="-mr-2 flex items-center">
                <div className="flex-shrink-0">
                  <a
                    href="/"
                    onClick={scrollToGetInTouch}
                    className="relative inline-flex items-center px-2.5 py-1 border border-transparent font-medium rounded-md text-white bg-blue-500 shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t('navigation.get_in_touch')}
                  </a>
                </div>
                <div className="sm:hidden">
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden">
            <div className="pt-2 pb-3 space-y-1">
              <a
                href="/"
                className="bg-indigo-50 border-blue-500 text-blue-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
              >
                {t('navigation.carbon_cap')}
              </a>
              <OutboundLink
                eventLabel="calculator.dovu.earth"
                to="https://calculator.dovu.earth/"
                target="_blank"
                rel="nofollow"
                className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
              >
                {t('navigation.calculator')}
              </OutboundLink>
              <OutboundLink
                eventLabel="docs.dovu.market"
                to="https://docs.dovu.market/"
                target="_blank"
                rel="nofollow"
                className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
              >
                {t('navigation.developer')}
              </OutboundLink>
              <OutboundLink
                eventLabel="dovu.earth"
                to="https://dovu.earth/"
                target="_blank"
                rel="nofollow"
                className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
              >
                {t('navigation.learn')}
              </OutboundLink>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}
